/**
 * The regional pricing offer, mirrored from brand's `lib/pricing/promos.ts`.
 *
 * Brand resolves the visitor's country from a CDN header its middleware copies
 * onto the `fb_country` cookie. That cookie is host-only on the marketing
 * domain, our API sits behind neither CloudFront nor Cloudflare, and no country
 * is stored on the user — so inside Studio the browser's own timezone is the
 * only signal available without new infrastructure.
 *
 * A guess is acceptable here because the code is not a secret: it is printed on
 * the public pricing page and Stripe accepts it typed into any checkout, so the
 * worst a wrong reading does is withhold a discount the user could claim by
 * hand. Stripe's coupon remains the authority on what is actually charged.
 *
 * The code and the country list must be kept in step with brand's `promos.ts`
 * by hand — quoting a discount here that brand does not honour, or the reverse,
 * is the failure mode to watch for.
 */

export const REGIONAL_PROMO_CODE = 'USEFB30'

// `Asia/Calcutta` is the deprecated alias for `Asia/Kolkata` and is still what
// some older browsers report.
const COUNTRY_TIMEZONES = {
  'Asia/Kolkata': 'IN',
  'Asia/Calcutta': 'IN',
  'Asia/Yerevan': 'AM'
}

/**
 * The visitor's country, or `null` when the timezone does not name one we have
 * an offer for. Never guesses a default region.
 */
export const countryFromTimezone = () => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    return COUNTRY_TIMEZONES[timezone] || null
  } catch (error) {
    // A browser without a resolvable timezone gets no offer rather than a throw
    // on the way into checkout.
    return null
  }
}

/**
 * The promo code to send with a checkout, or `undefined` so it passes straight
 * through to `createSession` without the caller branching.
 */
export const regionalPromoCode = () => {
  return countryFromTimezone() ? REGIONAL_PROMO_CODE : undefined
}
